<section class="hero px-6 py-24 sm:px-10 sm:py-28">
  <div class="mx-auto max-w-3xl">
    <span class="rise eyebrow mb-5 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold">
      A ghoulish tradition, now open to everyone
    </span>
    <h1
      class="rise font-display text-[2.6rem] font-black leading-[1.03] tracking-tight text-parchment sm:text-6xl lg:text-7xl"
      style="animation-delay: 0.05s"
    >
      Pick the famous.<br />See who outlives the year.
    </h1>
    <p
      class="rise mt-6 max-w-xl text-lg text-dusk"
      style="animation-delay: 0.2s"
    >
      A ghoul pool is a lighthearted celebrity death pool: you and your friends each pick public
      figures you think won't see the year out, and score points as the year unfolds. Ghoul Pool
      is where you run one &mdash; bragging rights, a running scoreboard, and the occasional prize.
      Nobody wins by wishing anyone ill; we just like a good wager on the inevitable.
    </p>
    <div class="rise mt-10 flex flex-wrap gap-4" style="animation-delay: 0.35s">
      @if (auth.isAuthenticated()) {
        <a
          routerLink="/competitions"
          class="inline-flex items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-6 py-3 text-sm font-bold text-ink transition hover:-translate-y-px"
          >Go to My Competitions</a
        >
      } @else {
        <a
          routerLink="/register"
          class="inline-flex items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-6 py-3 text-sm font-bold text-ink transition hover:-translate-y-px"
          >Start Free — Admit One</a
        >
        <a
          routerLink="/"
          fragment="how"
          class="inline-flex items-center justify-center rounded-[3px] border border-hairline px-6 py-3 text-sm font-semibold text-parchment transition hover:-translate-y-px hover:border-parchment"
          >See How It Works</a
        >
      }
    </div>
    <p class="rise mt-5 text-[0.82rem] text-dusk" style="animation-delay: 0.45s">
      Free tier: 1 competition, 4 contestants. No card required.
    </p>
  </div>
</section>

<section id="what" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-2xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >New to ghoul pools?</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Fantasy football for the famously mortal.
      </h2>
    </div>
    <div class="grid gap-8 md:grid-cols-2">
      <p class="leading-relaxed text-dusk">
        A ghoul pool is an old, slightly macabre party game. Every player drafts a list of celebrities and other public figures. When someone on your list dies, you score. When the year ends, whoever has the most points wins.
      </p>
      <p class="leading-relaxed text-dusk">
        It's less grim than it sounds. The fun is in the research, the trash talk, and the group chat that lights up when a name from your list starts trending. Only public figures are fair game, and nobody is rooting for anyone &mdash; you're just trying to judge the inevitable better than your friends do.
      </p>
    </div>
  </div>
</section>

<section id="story" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-2xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Where it comes from</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        From a shop counter to your group chat.
      </h2>
    </div>
    <div class="grid gap-8 md:grid-cols-2">
      <p class="leading-relaxed text-dusk">
        Ghoulish Mortals, a shop in Saint Charles, IL, has run its own Ghoul Pool for years &mdash; a long-running annual competition among the shop's regulars, with a scoreboard everyone watches all year.
      </p>
      <p class="leading-relaxed text-dusk">
        Along the way, plenty of people asked whether they could run a pool of their own. So we opened the doors: the same scoring and leaderboard the shop uses, available to anyone who wants to host one for friends, family, coworkers, or a whole community.
      </p>
    </div>
  </div>
</section>

<section id="how" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-2xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >The rules of the game</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Three steps, one long year of watching the news.
      </h2>
      <p class="mt-4 text-dusk">
        Every Ghoul Pool runs the same simple shape, whether it's two friends or a whole community.
      </p>
    </div>
    <div class="grid gap-5 md:grid-cols-3">
      <div class="step rounded border border-hairline bg-panel p-7">
        <span class="tabular-nums font-mono text-gold">01</span>
        <h3 class="mt-6 font-display text-xl font-semibold text-parchment">Join or start a pool</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Host your own and set the rules &mdash; the dates, how many picks each player gets, the prizes &mdash; or join a friend's pool with their invite code.
        </p>
      </div>
      <div class="step rounded border border-hairline bg-panel p-7">
        <span class="tabular-nums font-mono text-gold">02</span>
        <h3 class="mt-6 font-display text-xl font-semibold text-parchment">Draft your picks</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Search the celebrity catalog and lock in your picks. The host decides how many &mdash; a dozen, half a dozen, whatever suits the group &mdash; plus alternates who step in if one of your picks dies before picks lock.
        </p>
      </div>
      <div class="step rounded border border-hairline bg-panel p-7">
        <span class="tabular-nums font-mono text-gold">03</span>
        <h3 class="mt-6 font-display text-xl font-semibold text-parchment">Watch the leaderboard</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Scores update automatically all year, and you'll get an email when one of your picks dies. Open any player's row to see exactly where every point came from.
        </p>
      </div>
    </div>
  </div>
</section>

<section id="scoring" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-2xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Scoring</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Youth pays. Timing pays more.
      </h2>
      <p class="mt-4 text-dusk">
        Every death scores, and the leaderboard can turn on a single headline.
      </p>
    </div>
    <div class="rounded border border-hairline bg-panel p-7">
      <h3 class="font-display text-xl font-semibold text-parchment">Age sets the base score</h3>
      <p class="mt-3 max-w-2xl text-[0.95rem] text-dusk">
        When a pick dies, they score points based on how young they were: the younger the pick, the
        bigger the score. Nobody wants to be the one who picked someone who lived to 104.
      </p>
    </div>

    <h3 class="mt-12 font-display text-xl font-semibold text-parchment">In every pool</h3>
    <p class="mt-2 max-w-2xl text-sm text-dusk">
      Achievements are bonus awards on top of age points. These three come standard.
    </p>
    <div class="mt-5 grid gap-4 md:grid-cols-3">
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">First to Die</h4>
          <p class="mt-2 text-sm text-dusk">Everyone who picked the first person on the list to die.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Last Gasp</h4>
          <p class="mt-2 text-sm text-dusk">Everyone who picked the most recent death &mdash; so the lead can change right up to the end.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Grim Reaper</h4>
          <p class="mt-2 text-sm text-dusk">The player with the most points when the pool ends.</p>
        </div>
    </div>

    <h3 class="mt-12 font-display text-xl font-semibold text-parchment">Optional extras</h3>
    <p class="mt-2 max-w-2xl text-sm text-dusk">
      Hosts on a paid plan can add more from this list, or invent their own &mdash; and attach
      points or a real prize to any of them.
    </p>
    <div class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Friday the 13th</h4>
          <p class="mt-2 text-sm text-dusk">A pick dies on a Friday the 13th.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Halloween</h4>
          <p class="mt-2 text-sm text-dusk">A pick dies on October 31st.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Unhappy Birthday</h4>
          <p class="mt-2 text-sm text-dusk">A pick dies on their own birthday.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">You Better Watch Out</h4>
          <p class="mt-2 text-sm text-dusk">A pick dies on Christmas Day.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Random Pick</h4>
          <p class="mt-2 text-sm text-dusk">Only one player in the pool picked the person who died.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">New Blood</h4>
          <p class="mt-2 text-sm text-dusk">A pick dies in January, after the picks have locked.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Reap Baby Reap</h4>
          <p class="mt-2 text-sm text-dusk">The player with the most picks who have died.</p>
        </div>
        <div class="rounded border border-hairline bg-panel p-5">
          <h4 class="font-display text-base font-semibold text-parchment">Dropping Like Flies</h4>
          <p class="mt-2 text-sm text-dusk">Three or more of your picks die in a row, with none of anyone else's in between.</p>
        </div>
    </div>
  </div>
</section>

<section id="host" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-2xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Hosting</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Run it your way.
      </h2>
      <p class="mt-4 text-dusk">
        Every group plays a little differently, so the pool bends to fit yours.
      </p>
    </div>
    <div class="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
      <div class="step rounded border border-hairline bg-panel p-7">
        <h3 class="font-display text-xl font-semibold text-parchment">Your rules</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Pick the start and end dates, the cutoff when picks lock, how many picks each player gets, and how many alternates they may name.
        </p>
      </div>
      <div class="step rounded border border-hairline bg-panel p-7">
        <h3 class="font-display text-xl font-semibold text-parchment">Your prizes</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Give any achievement points or a real prize &mdash; a gift card, a mystery box, or just bragging rights.
        </p>
      </div>
      <div class="step rounded border border-hairline bg-panel p-7">
        <h3 class="font-display text-xl font-semibold text-parchment">Your people</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          Share an invite code to bring players in, and name cohosts to help you run things.
        </p>
      </div>
      <div class="step rounded border border-hairline bg-panel p-7">
        <h3 class="font-display text-xl font-semibold text-parchment">Your scoreboard</h3>
        <p class="mt-3 text-[0.95rem] text-dusk">
          A live leaderboard that explains itself, and an email to players whenever one of their picks dies.
        </p>
      </div>
    </div>
  </div>
</section>

<section id="tiers" class="border-t border-hairline px-6 py-20 sm:px-10">
  <div class="mx-auto max-w-6xl">
    <div class="mb-12 max-w-xl">
      <span class="mb-4 block text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold"
        >Membership</span
      >
      <h2 class="font-display text-3xl font-semibold text-parchment sm:text-4xl">
        Start free. Grow the pool when you're ready.
      </h2>
      <p class="mt-4 text-dusk">
        Every account starts on the free tier — no trial clock, no card on file.
      </p>
    </div>

    <div class="grid gap-6 md:grid-cols-2">
      <div class="relative flex flex-col rounded-md border border-gold-dim bg-panel p-9 shadow-[inset_0_0_0_1px_rgba(201,162,39,0.18)]">
        <div
          class="badge-stamp absolute -top-3 right-6 flex h-[4.6rem] w-[4.6rem] -rotate-[8deg] items-center justify-center rounded-full text-center"
        >
          <span class="font-mono text-[0.62rem] uppercase leading-tight tracking-wide text-parchment"
            >Admit<br />One</span
          >
        </div>
        <div class="flex items-start justify-between gap-4">
          <span class="font-display text-2xl font-semibold text-parchment">Free</span>
          <span class="font-mono tabular-nums text-gold">$0</span>
        </div>
        <ul class="mt-6 flex flex-1 flex-col gap-3">
          <li class="flex items-baseline gap-3 text-[0.95rem] text-parchment">
            <span class="font-mono text-[0.85rem] text-gold">·</span> 1 active competition
          </li>
          <li class="flex items-baseline gap-3 text-[0.95rem] text-parchment">
            <span class="font-mono text-[0.85rem] text-gold">·</span> Up to 4 contestants
          </li>
          <li class="flex items-baseline gap-3 text-[0.95rem] text-parchment">
            <span class="font-mono text-[0.85rem] text-gold">·</span> Full celebrity catalog &amp; search
          </li>
          <li class="flex items-baseline gap-3 text-[0.95rem] text-parchment">
            <span class="font-mono text-[0.85rem] text-gold">·</span> Live scoring &amp; leaderboard
          </li>
        </ul>
        <a
          routerLink="/register"
          class="mt-8 inline-flex w-full items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-6 py-3 text-sm font-bold text-ink transition hover:-translate-y-px"
          >Start Free</a
        >
      </div>

      <div class="flex flex-col rounded-md border border-dashed border-hairline bg-panel p-9 opacity-70">
        <div class="flex items-start justify-between gap-4">
          <span class="font-display text-2xl font-semibold text-parchment">Paid Tiers</span>
          <span class="font-mono tabular-nums text-dusk">Soon</span>
        </div>
        <ul class="mt-6 flex flex-1 flex-col gap-3">
          <li class="flex items-baseline gap-3 text-[0.95rem] text-dusk">
            <span class="font-mono text-[0.85rem] text-dusk">·</span> More active competitions
          </li>
          <li class="flex items-baseline gap-3 text-[0.95rem] text-dusk">
            <span class="font-mono text-[0.85rem] text-dusk">·</span> Larger contestant rosters
          </li>
          <li class="flex items-baseline gap-3 text-[0.95rem] text-dusk">
            <span class="font-mono text-[0.85rem] text-dusk">·</span> Extra scoring &amp; bonus options
          </li>
        </ul>
        <div class="mt-7 border-t border-dashed border-hairline pt-5">
          <span class="text-[0.7rem] uppercase tracking-[0.12em] text-dusk">Pricing not yet set</span>
        </div>
        <a
          class="pointer-events-none mt-8 inline-flex w-full items-center justify-center rounded-[3px] border border-hairline px-6 py-3 text-sm font-semibold text-parchment opacity-60"
          >Coming Soon</a
        >
      </div>
    </div>
  </div>
</section>
